import React, {
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import {
	Badge,
	Button,
	CloseButton,
	Col,
	Collapse,
	Form,
	Row,
} from 'react-bootstrap';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import {
	activeFilterChips,
	ADVANCED_FILTERS,
	countActiveFilters,
	PRIMARY_FILTERS,
	resolveFilterLayout,
	visibleChipCount,
	FilterChip,
} from 'src/vehicleList/filterGroups';
import ToolTip from 'src/components/Tooltip/Tooltip';

interface GroupedVehicleFilterFieldsProps {
	headerGroups: any[];
	filterState: Record<string, any>;
	appliedState: Record<string, any>;
	setFilterState: (updater: any) => void;
	filterOptions?: Record<string, any[]>;
	userRole?: string;
	advancedOpen: boolean;
	onToggleAdvanced: () => void;
	onRemoveFilter: (fieldId: string) => void;
	onApplyFilter: (fieldId: string, value: unknown) => void;
	actions?: React.ReactNode;
}

const ADVANCED_PANEL_ID = 'advanced-filters-panel';
const MORE_CHIPS_ID = 'filter-chips-more-toggle';

const FilterIcon = () => (
	<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
		<g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
			<path d="M4 6h9M17 6h3M4 12h3M11 12h9M4 18h11M19 18h1" />
			<circle cx="15" cy="6" r="2" />
			<circle cx="9" cy="12" r="2" />
			<circle cx="17" cy="18" r="2" />
		</g>
	</svg>
);

const SearchIcon = () => (
	<svg
		className="vehicle-search-icon"
		viewBox="0 0 24 24"
		width="18"
		height="18"
		aria-hidden="true"
	>
		<g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
			<circle cx="10.5" cy="10.5" r="6.5" />
			<path d="M15.5 15.5L20 20" />
		</g>
	</svg>
);

const GroupedVehicleFilterFields: React.FC<GroupedVehicleFilterFieldsProps> = ({
	headerGroups,
	filterState,
	appliedState,
	setFilterState,
	filterOptions,
	userRole,
	advancedOpen,
	onToggleAdvanced,
	onRemoveFilter,
	onApplyFilter,
	actions,
}) => {
	const { t } = useTranslation();

	const columnsById = useMemo(
		() =>
			new Map<string, any>(
				headerGroups
					.flatMap((headerGroup) => headerGroup.headers)
					.filter((column) => column.PrimaryFilter || column.SecondaryFilter)
					.map((column) => [column.id, column])
			),
		[headerGroups]
	);

	const { primary, advanced } = useMemo(
		() =>
			resolveFilterLayout(
				ADVANCED_FILTERS,
				PRIMARY_FILTERS,
				Array.from(columnsById.keys()),
				userRole
			),
		[columnsById, userRole]
	);

	const chips = useMemo(() => {
		const labels: Record<string, string> = {
			search: t('search', { defaultValue: 'Cerca' }),
		};
		columnsById.forEach((column, id) => {
			labels[id] = typeof column.Header === 'string' ? column.Header : id;
		});

		return activeFilterChips(
			appliedState,
			['search', ...primary, ...advanced],
			labels,
			filterOptions
		);
	}, [appliedState, columnsById, primary, advanced, filterOptions, t]);

	const activeAdvanced = countActiveFilters(advanced, appliedState);

	const searchRef = useRef<HTMLInputElement>(null);
	const chipsRowRef = useRef<HTMLDivElement>(null);
	const chipsLabelRef = useRef<HTMLSpanElement>(null);
	const chipsMeasureRef = useRef<HTMLDivElement>(null);
	const moreButtonRef = useRef<HTMLButtonElement>(null);
	const morePopoverRef = useRef<HTMLDivElement>(null);
	const [moreOpen, setMoreOpen] = useState(false);
	const [visibleCount, setVisibleCount] = useState<number | null>(null);

	useLayoutEffect(() => {
		const row = chipsRowRef.current;
		const measure = chipsMeasureRef.current;
		if (!row || !measure || typeof ResizeObserver === 'undefined') return;

		const width = (el: Element | null) =>
			el?.getBoundingClientRect().width ?? 0;
		const compute = () => {
			const available = row.clientWidth;
			if (!available) return setVisibleCount(null);

			const gap = parseFloat(getComputedStyle(row).columnGap) || 0;
			setVisibleCount(
				visibleChipCount(
					Array.from(measure.querySelectorAll('[data-measure-chip]')).map(
						width
					),
					available - width(chipsLabelRef.current) - gap,
					width(measure.querySelector('[data-measure-more]')),
					gap
				)
			);
		};

		compute();
		const observer = new ResizeObserver(compute);
		observer.observe(row);
		Array.from(measure.children).forEach((child) => observer.observe(child));
		return () => observer.disconnect();
	}, [chips]);

	const visibleChips = chips.slice(0, visibleCount ?? chips.length);
	const hiddenChips = chips.slice(visibleChips.length);

	useEffect(() => {
		if (!hiddenChips.length) setMoreOpen(false);
	}, [hiddenChips.length]);

	const removedChipIndex = useRef<number | null>(null);

	useEffect(() => {
		const index = removedChipIndex.current;
		if (index === null) return;
		removedChipIndex.current = null;

		const target = chips[Math.min(index, chips.length - 1)];
		const close =
			target &&
			[chipsRowRef.current, morePopoverRef.current]
				.map((root) =>
					root?.querySelector<HTMLElement>(`[data-chip-close="${target.id}"]`)
				)
				.find(Boolean);
		(
			close ??
			(target ? moreButtonRef.current : null) ??
			searchRef.current
		)?.focus();
	}, [chips]);

	const pendingFocusField = useRef<string | null>(null);

	const focusFieldControl = (id: string) => {
		const root = searchRef.current?.closest('.filters-card') ?? document;
		root
			.querySelector(`label[for="${id}"]`)
			?.parentElement?.querySelector<HTMLElement>(
				'input:not([type="hidden"]), select, textarea'
			)
			?.focus();
	};

	const focusField = (id: string) => {
		setMoreOpen(false);
		if (id === 'search') return searchRef.current?.focus();
		if (advanced.includes(id) && !advancedOpen) {
			pendingFocusField.current = id;
			onToggleAdvanced();
			return;
		}
		focusFieldControl(id);
	};

	const handleAdvancedEntered = () => {
		if (!pendingFocusField.current) return;
		focusFieldControl(pendingFocusField.current);
		pendingFocusField.current = null;
	};

	const renderChip = (chip: FilterChip, index: number, measure = false) => (
		<Badge
			key={chip.id}
			bg="light"
			text="dark"
			className="filter-chip"
			{...(measure
				? { 'data-measure-chip': true }
				: { 'data-testid': 'filter-chip' })}
		>
			<button
				type="button"
				className="filter-chip-edit"
				tabIndex={measure ? -1 : undefined}
				onClick={measure ? undefined : () => focusField(chip.id)}
			>
				{chip.label}: <b>{chip.value}</b>
			</button>
			<CloseButton
				tabIndex={measure ? -1 : undefined}
				data-chip-close={measure ? undefined : chip.id}
				aria-label={`${t('remove_filter', {
					defaultValue: 'Rimuovi filtri',
				})} ${chip.label}`}
				onClick={
					measure
						? undefined
						: () => {
								removedChipIndex.current = index;
								onRemoveFilter(chip.id);
						  }
				}
			/>
		</Badge>
	);

	const renderPrimary = (id: string) => {
		const column = columnsById.get(id);
		const label = typeof column.Header === 'string' ? column.Header : id;
		const options = filterOptions?.[id] ?? [];
		const selected: string[] = filterState[id] ?? [];
		const all = {
			value: '',
			label: `${label}: ${t('all', { defaultValue: 'Tutti' })}`,
		};
		const current = selected.length
			? {
					value: selected.join(','),
					label: selected
						.map(
							(value) =>
								options.find((option) => option.value === value)?.label ?? value
						)
						.join(', '),
			  }
			: all;

		return (
			<div key={id} className="primary-filter">
				<Form.Label htmlFor={id} visuallyHidden>
					{column.render('Header')}
				</Form.Label>
				<Select
					inputId={id}
					className="primary-filter-select"
					classNamePrefix="primary-filter"
					menuPosition="fixed"
					isSearchable={false}
					value={current}
					options={[all, ...options]}
					onChange={(option: any) =>
						onApplyFilter(id, option?.value ? [option.value] : [])
					}
				/>
			</div>
		);
	};

	const renderField = (id: string) => {
		const column = columnsById.get(id);
		const filterKey = column.PrimaryFilter
			? 'PrimaryFilter'
			: 'SecondaryFilter';

		return (
			<Col key={id}>
				<Form.Group className="mb-3">
					<div>
						<Form.Label htmlFor={id}>{column.render('Header')}</Form.Label>
						{column.render(filterKey, {
							props: {
								filterState,
								setFilterState,
								type: column.inputType,
								isCreatable: column.isCreatable,
								options: filterOptions?.[id],
							},
						})}
					</div>
				</Form.Group>
			</Col>
		);
	};

	return (
		<>
			<div className="filter-bar">
				<div className="vehicle-search">
					<Form.Label htmlFor="vehicle-search" visuallyHidden>
						{t('search', { defaultValue: 'Cerca' })}
					</Form.Label>
					<SearchIcon />
					<Form.Control
						ref={searchRef}
						id="vehicle-search"
						type="text"
						placeholder={t('vehicle_list_search_placeholder', {
							defaultValue: 'Cerca ordine, telaio, targa o codice costruttore…',
						})}
						value={filterState.search ?? ''}
						onChange={(e) =>
							setFilterState((prev) => ({ ...prev, search: e.target.value }))
						}
					/>
				</div>
				{primary.map(renderPrimary)}
				{advanced.length > 0 && (
					<Button
						variant="link"
						className="advanced-filters-toggle"
						data-advanced-filters-toggle
						aria-expanded={advancedOpen}
						aria-controls={ADVANCED_PANEL_ID}
						onClick={onToggleAdvanced}
					>
						<FilterIcon />
						{t('advanced_filters', { defaultValue: 'Filtri avanzati' })}
						{activeAdvanced > 0 && (
							<Badge pill bg="primary">
								{activeAdvanced}
							</Badge>
						)}
					</Button>
				)}
			</div>

			<div className="filter-chips-row" ref={chipsRowRef}>
				{chips.length > 0 && (
					<>
						<span className="filter-chips-label" ref={chipsLabelRef}>
							{t('active_filters', { defaultValue: 'Filtri attivi' })}
						</span>
						{visibleChips.map((chip, index) => renderChip(chip, index))}
						{hiddenChips.length > 0 && (
							<>
								<Button
									ref={moreButtonRef}
									id={MORE_CHIPS_ID}
									variant="outline-secondary"
									size="sm"
									className="filter-chips-more"
									aria-expanded={moreOpen}
									aria-label={t('more_active_filters', {
										count: hiddenChips.length,
										defaultValue: 'Altri {{count}} filtri attivi',
									})}
									onClick={() => setMoreOpen((open) => !open)}
								>
									+{hiddenChips.length}
								</Button>
								<ToolTip
									anchorId={MORE_CHIPS_ID}
									className="popover"
									opacity={1}
									place="bottom-end"
									clickable
									openOnClick
									isOpen={moreOpen}
									setIsOpen={setMoreOpen}
									globalCloseEvents={{
										escape: true,
										clickOutsideAnchor: true,
									}}
								>
									<div className="filter-chips-popover" ref={morePopoverRef}>
										{hiddenChips.map((chip, index) =>
											renderChip(chip, visibleChips.length + index)
										)}
									</div>
								</ToolTip>
							</>
						)}
						<div
							className="filter-chips-measure"
							ref={chipsMeasureRef}
							aria-hidden="true"
						>
							{chips.map((chip, index) => renderChip(chip, index, true))}
							<Button
								variant="outline-secondary"
								size="sm"
								className="filter-chips-more"
								tabIndex={-1}
								data-measure-more
							>
								+{chips.length}
							</Button>
						</div>
					</>
				)}
			</div>

			<Collapse in={advancedOpen} onEntered={handleAdvancedEntered}>
				<div id={ADVANCED_PANEL_ID}>
					<div className="advanced-filters-panel">
						<Row xs={1} sm={2} lg={4}>
							{advanced.map(renderField)}
						</Row>
						{actions && <div className="filter-actions">{actions}</div>}
					</div>
				</div>
			</Collapse>
		</>
	);
};

export default GroupedVehicleFilterFields;
